import React, { useState, useEffect } from 'react';
import { tasksAPI, casesAPI, teamAPI } from '../lib/api';
import { formatDate, getStatusColor, getPriorityColor } from '../lib/utils';
import { toast } from 'sonner';
import { 
  CheckSquare, 
  Plus, 
  Search,
  Calendar,
  Clock,
  Check,
  Edit,
  Trash2,
  AlertTriangle,
  User,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { PageLoader } from '../components/common/LoadingSpinner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { cn } from '../lib/utils';

export const Tasks = () => {
  const [tasks, setTasks] = useState([]);
  const [cases, setCases] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    case_id: '',
    assigned_to: '',
    priority: 'medium',
    due_date: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, casesRes, teamRes] = await Promise.all([
        tasksAPI.getAll(),
        casesAPI.getAll(),
        teamAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setTasks(tasksRes.data);
      setCases(casesRes.data);
      setTeamMembers(teamRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = { ...formData };
      if (data.due_date) {
        data.due_date = new Date(data.due_date).toISOString();
      } else {
        delete data.due_date;
      }
      if (!data.case_id) delete data.case_id;
      if (!data.assigned_to) delete data.assigned_to;
      
      if (editingTask) {
        await tasksAPI.update(editingTask.task_id, data);
        toast.success('Task updated successfully');
      } else {
        await tasksAPI.create(data);
        toast.success('Task created successfully');
      }
      fetchData();
      resetForm();
    } catch (error) {
      console.error('Failed to save task:', error);
      toast.error('Failed to save task');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    setFormData({
      title: task.title,
      description: task.description || '',
      case_id: task.case_id || '',
      assigned_to: task.assigned_to || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.slice(0, 16) : '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    try {
      await tasksAPI.update(taskId, { status: 'cancelled' });
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await tasksAPI.complete(taskId);
      toast.success('Task completed!');
      fetchData();
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast.error('Failed to complete task');
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', case_id: '', assigned_to: '', priority: 'medium', due_date: '' });
    setEditingTask(null);
    setIsDialogOpen(false);
  };

  const getCaseName = (caseId) => {
    const caseItem = cases.find(c => c.case_id === caseId);
    return caseItem?.title || '';
  };

  const getAssigneeName = (userId) => {
    const member = teamMembers.find(m => m.user_id === userId);
    return member?.name || '';
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const isDueSoon = (dueDate) => {
    if (!dueDate) return false;
    const due = new Date(dueDate);
    const now = new Date();
    const diff = (due - now) / (1000 * 60 * 60 * 24);
    return diff >= 0 && diff <= 3;
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || task.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority && task.status !== 'cancelled';
  });

  const pendingTasks = filteredTasks.filter(t => t.status === 'pending' || t.status === 'in_progress');
  const completedTasks = filteredTasks.filter(t => t.status === 'completed');
  
  // Sort pending tasks by priority and due date
  const sortedPendingTasks = [...pendingTasks].sort((a, b) => {
    const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    if (a.due_date && b.due_date) return new Date(a.due_date) - new Date(b.due_date);
    return a.due_date ? -1 : 1;
  });

  const overdueTasks = sortedPendingTasks.filter(t => isOverdue(t.due_date));
  const upcomingTasks = sortedPendingTasks.filter(t => !isOverdue(t.due_date));

  if (loading) {
    return <PageLoader text="Loading tasks..." />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Tasks</h1>
          <p className="text-slate-500 mt-1">Manage your work items and deadlines</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setIsDialogOpen(true); }}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-task-btn">
              <Plus className="h-4 w-4" />
              Add Task
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]">
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Edit Task' : 'Create New Task'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  data-testid="task-title-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  data-testid="task-description-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="case_id">Related Case</Label>
                  <Select
                    value={formData.case_id || "none"}
                    onValueChange={(value) => setFormData({ ...formData, case_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger data-testid="task-case-select">
                      <SelectValue placeholder="Select case" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {cases.map((caseItem) => (
                        <SelectItem key={caseItem.case_id} value={caseItem.case_id}>
                          {caseItem.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="assigned_to">Assign To</Label>
                  <Select
                    value={formData.assigned_to || "unassigned"}
                    onValueChange={(value) => setFormData({ ...formData, assigned_to: value === "unassigned" ? "" : value })}
                  >
                    <SelectTrigger data-testid="task-assignee-select">
                      <SelectValue placeholder="Select assignee" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {teamMembers.map((member) => (
                        <SelectItem key={member.user_id} value={member.user_id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger data-testid="task-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_date">Due Date</Label>
                  <Input
                    id="due_date"
                    type="datetime-local"
                    value={formData.due_date}
                    onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                    data-testid="task-duedate-input"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                <Button type="submit" data-testid="save-task-btn">
                  {editingTask ? 'Update Task' : 'Create Task'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <CheckSquare className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{tasks.filter(t => t.status !== 'cancelled').length}</p>
                <p className="text-xs text-slate-500">Total Tasks</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{pendingTasks.length}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{overdueTasks.length}</p>
                <p className="text-xs text-slate-500">Overdue</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{completedTasks.length}</p>
                <p className="text-xs text-slate-500">Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-tasks-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Priority" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Priority</SelectItem>
            <SelectItem value="urgent">Urgent</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Overdue Tasks Alert */}
      {overdueTasks.length > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="overdue-tasks-alert">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Overdue Tasks ({overdueTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {overdueTasks.map((task) => (
              <div
                key={task.task_id}
                className="p-3 rounded-lg bg-white border border-red-200 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => handleComplete(task.task_id)}
                    className="h-5 w-5 rounded border-2 border-red-300 hover:border-green-500 hover:bg-green-50 transition-colors flex items-center justify-center"
                    data-testid={`complete-overdue-${task.task_id}`}
                  >
                    <Check className="h-3 w-3 text-transparent hover:text-green-500" />
                  </button>
                  <div>
                    <p className="font-medium text-slate-900">{task.title}</p>
                    <p className="text-xs text-red-600">Due {formatDate(task.due_date)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    onClick={() => handleEdit(task)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-100"
                    onClick={() => handleDelete(task.task_id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Tasks Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Tasks */}
        <Card data-testid="pending-tasks-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-500" />
              Pending ({upcomingTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
            {upcomingTasks.length > 0 ? (
              upcomingTasks.map((task) => (
                <div
                  key={task.task_id}
                  className={cn(
                    "p-4 rounded-lg border transition-colors",
                    isDueSoon(task.due_date) 
                      ? "border-amber-200 bg-amber-50" 
                      : "border-slate-200 hover:border-slate-300"
                  )}
                  data-testid={`task-item-${task.task_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 flex-1">
                      <button
                        onClick={() => handleComplete(task.task_id)}
                        className="mt-1 h-5 w-5 rounded border-2 border-slate-300 hover:border-green-500 hover:bg-green-50 transition-colors flex items-center justify-center flex-shrink-0"
                        data-testid={`complete-task-${task.task_id}`}
                      >
                        <Check className="h-3 w-3 text-transparent hover:text-green-500" />
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900">{task.title}</p>
                        {task.description && (
                          <p className="text-sm text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                        )}
                        <div className="flex flex-wrap gap-2 mt-2">
                          {task.case_id && (
                            <Badge variant="outline" className="text-xs">
                              {getCaseName(task.case_id)}
                            </Badge>
                          )}
                          {task.assigned_to && (
                            <Badge variant="secondary" className="text-xs flex items-center gap-1">
                              <User className="h-3 w-3" />
                              {getAssigneeName(task.assigned_to)}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-2 ml-2">
                      <span className={cn(
                        "text-xs font-medium capitalize px-2 py-1 rounded",
                        task.priority === 'urgent' && "bg-red-100 text-red-700",
                        task.priority === 'high' && "bg-orange-100 text-orange-700",
                        task.priority === 'medium' && "bg-blue-100 text-blue-700",
                        task.priority === 'low' && "bg-slate-100 text-slate-700"
                      )}>
                        {task.priority}
                      </span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0"
                        onClick={() => handleEdit(task)}
                        data-testid={`edit-task-${task.task_id}`}
                      >
                        <Edit className="h-3.5 w-3.5" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDelete(task.task_id)}
                        data-testid={`delete-task-${task.task_id}`}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  {task.due_date && (
                    <div className={cn(
                      "mt-3 flex items-center gap-1 text-xs",
                      isDueSoon(task.due_date) ? "text-amber-600 font-medium" : "text-slate-500"
                    )}>
                      <Calendar className="h-3 w-3" />
                      <span>Due {formatDate(task.due_date)}</span>
                      {isDueSoon(task.due_date) && <span className="ml-1">(Due soon!)</span>}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>No pending tasks</p>
                <Button 
                  variant="link" 
                  className="mt-2"
                  onClick={() => setIsDialogOpen(true)}
                >
                  Create your first task
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Completed Tasks */}
        <Card data-testid="completed-tasks-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Check className="h-5 w-5 text-green-500" />
              Completed ({completedTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 max-h-[500px] overflow-y-auto">
            {completedTasks.length > 0 ? (
              completedTasks.slice(0, 15).map((task) => (
                <div
                  key={task.task_id}
                  className="p-4 rounded-lg bg-slate-50 border border-slate-100"
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-1 h-5 w-5 rounded bg-green-500 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-500 line-through">{task.title}</p>
                      {task.completed_at && (
                        <p className="text-xs text-slate-400 mt-1">
                          Completed {formatDate(task.completed_at)}
                        </p>
                      )}
                      {task.case_id && (
                        <Badge variant="outline" className="mt-2 text-xs opacity-60">
                          {getCaseName(task.case_id)}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-slate-500">
                <CheckSquare className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                <p>No completed tasks yet</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Tasks;
